import { Progress as P } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { type Tone, toneClasses } from '../lib/tokens';

/** Hairline-bordered surface. Hierarchy comes from spacing and type, not shadows. */
export function Card(props: { children: ReactNode; className?: string; as?: 'section' | 'div' }) {
  const Tag = props.as ?? 'section';
  return (
    <Tag className={cn('rounded-lg border border-line bg-surface', props.className)}>
      {props.children}
    </Tag>
  );
}

export function CardHeader(props: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4 px-5 pb-3 pt-4', props.className)}>
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-ink">{props.title}</h2>
        {props.description ? (
          <p className="mt-0.5 text-sm text-ink-muted">{props.description}</p>
        ) : null}
      </div>
      {props.actions ? (
        <div className="flex shrink-0 items-center gap-2">{props.actions}</div>
      ) : null}
    </div>
  );
}

export function StatCard(props: {
  label: ReactNode;
  value: ReactNode;
  icon?: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  loading?: boolean;
}) {
  return (
    <Card className="flex items-start gap-4 p-5">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-ink-muted">{props.label}</p>
        {props.loading ? (
          <Skeleton className="mt-2 h-8 w-20" />
        ) : (
          <p className="tabular mt-1 text-2xl font-semibold tracking-tight text-ink">
            {props.value}
          </p>
        )}
        {props.hint ? <p className="mt-1 text-xs text-ink-subtle">{props.hint}</p> : null}
      </div>
      {props.icon ? (
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-lg',
            toneClasses[props.tone ?? 'primary'],
          )}
          aria-hidden="true"
        >
          {props.icon}
        </span>
      ) : null}
    </Card>
  );
}

export function Badge(props: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        toneClasses[props.tone ?? 'neutral'],
        props.className,
      )}
    >
      {props.children}
    </span>
  );
}

/** A state with a dot: the dot color is redundant with the text (never color alone). */
export function StatusBadge(props: { tone: Tone; children: ReactNode }) {
  return (
    <Badge tone={props.tone}>
      <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
      {props.children}
    </Badge>
  );
}

const AVATAR_TONES = [
  'bg-primary-soft text-primary',
  'bg-info-soft text-info',
  'bg-warning-soft text-warning',
  'bg-success-soft text-success',
];

export function initials(name: string): string {
  const parts = name
    .replace(/@.*/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);
  const letters =
    parts.length > 1 ? [parts[0]?.[0], parts.at(-1)?.[0]] : [parts[0]?.[0], parts[0]?.[1]];
  return letters.filter(Boolean).join('').toUpperCase() || '?';
}

export function Avatar(props: { name: string; size?: 'sm' | 'md'; className?: string }) {
  let hash = 0;
  for (const c of props.name) hash = (hash * 31 + c.charCodeAt(0)) | 0;
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
        AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length],
        props.size === 'sm' ? 'size-7 text-xs' : 'size-9 text-sm',
        props.className,
      )}
    >
      {initials(props.name)}
    </span>
  );
}

export function Progress(props: { value: number; label: string; className?: string }) {
  return (
    <P.Root
      value={props.value}
      aria-label={props.label}
      className={cn('h-2 w-full overflow-hidden rounded-full bg-surface-muted', props.className)}
    >
      <P.Indicator
        className="h-full rounded-full bg-primary transition-[width] duration-200"
        style={{ width: `${Math.max(0, Math.min(100, props.value))}%` }}
      />
    </P.Root>
  );
}

export function Skeleton(props: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('bl-shimmer block rounded-md bg-surface-muted', props.className)}
    />
  );
}
